import { useQuery } from '@tanstack/react-query';
import { forwardRef, useState } from 'react';
import { getProjectTdmAnalytics } from '@/api/analytics';
import type { IGetProjectsServiceResData } from '@/api/Sherpa/Project/modules/ProjectCore/types';
import ImageCircularAvatar from '@/components/UI/Avatar/ImageCircularAvatar';
import Badge from '@/components/UI/Badge';
import placeholderProjectThumb from '@/images/pictures/project-thumbnail-placeholder.png';
import { convertEnumToText, getInitials } from '@/utils';
import { getTdmCompleteOverCount } from '@/utils/analytics';
import styles from './MapViewProjectCard.module.css';

const MapViewProjectCard = forwardRef<
  HTMLLIElement,
  {
    project: IGetProjectsServiceResData;
    selectedProjectId: number;
    projectCount?: number;
    onClick?: () => void;
  }
>(({ project, selectedProjectId, projectCount, onClick }, ref) => {
  //Tanstack: version 5 query implementation
  const fetchProjectTdmAnalytics = useQuery({
    queryKey: [`project-tdm-analytics-${project.id}`, selectedProjectId],
    queryFn: () => getProjectTdmAnalytics(project.id),
  });
  const [isImageLoadError, setIsImageLoadError] = useState(false);
  const projectTdmAnalytics = fetchProjectTdmAnalytics?.data;
  const tasksStat = getTdmCompleteOverCount(projectTdmAnalytics?.tasks);
  const deliverablesStat = getTdmCompleteOverCount(projectTdmAnalytics?.deliverables);
  const milestonesStat = getTdmCompleteOverCount(projectTdmAnalytics?.milestones);

  return (
    <li
      ref={ref}
      onClick={onClick}
      className={`relative grid md:grid-cols-2 lg:grid-cols-1 2xl:grid-cols-2 flex-col w-full border-4 rounded-md cursor-pointer hover:border-primaryColor-lighter hover:bg-green-50 ${
        project.id === selectedProjectId && 'border-4 border-primaryColor-lighter bg-green-50'
      } `}
    >
      <div className='relative'>
        <span className='absolute top-2 right-2 capitalize'>
          <Badge status={project.stage ? project.stage : 'neutral'} text={convertEnumToText(project.status)} />
        </span>
        {project.thumbnailImageUrl && !isImageLoadError && (
          <img
            src={project.thumbnailImageUrl}
            alt='project-thumbnail'
            className='object-cover h-64 w-full rounded-l-sm'
            onError={() => setIsImageLoadError(true)}
          />
        )}
        {(!project.thumbnailImageUrl || isImageLoadError) && (
          <img src={placeholderProjectThumb} alt='project-thumbnail' className='object-cover h-64 w-full' />
        )}
      </div>
      <div className='h-full p-4 flex flex-col justify-between gap-4'>
        <div>
          <h4 className='font-bold break-all'>{project.name}</h4>
          <p className='text-neutralColor-normal text-xs wrap-break-word'>{project.fullAddress}</p>
        </div>
        <div className='flex flex-col gap-2'>
          <div className='flex items-center gap-3'>
            <ImageCircularAvatar imageUrl={project.projectLeader.profileImageUrl} fallbackText={getInitials(project.projectLeader.name)} />
            <div className='flex flex-col'>
              <span className=''>{project.projectLeader.name}</span>
              <span className='font-semibold'>Project Lead</span>
            </div>
          </div>
          <div className='flex w-full justify-between'>
            <div className='flex flex-col justify-between py-2'>
              <span>Tasks</span>
              <span
                className={`text-center  
                ${deliverablesStat.percentComplete < 100 && styles.healthy}
                ${deliverablesStat.percentComplete < 50 && styles.warning}
                ${deliverablesStat.percentComplete < 20 && styles.danger}
                `}
              >
                {tasksStat.completeCount}/{tasksStat.totalCount}
              </span>
            </div>
            <div className='flex flex-col justify-between py-2'>
              <span>Deliverables</span>
              <span
                className={`text-center 
                ${deliverablesStat.percentComplete < 100 && styles.healthy}
            ${deliverablesStat.percentComplete < 50 && styles.warning}
            ${deliverablesStat.percentComplete < 20 && styles.danger}
                `}
              >
                {deliverablesStat.completeCount}/{deliverablesStat.totalCount}
              </span>
            </div>
            <div className='flex flex-col justify-between py-2'>
              <span>Milestones</span>
              <span
                className={`text-center 
                ${deliverablesStat.percentComplete < 100 && styles.healthy}
            ${deliverablesStat.percentComplete < 50 && styles.warning}
            ${deliverablesStat.percentComplete < 20 && styles.danger}
                `}
              >
                {milestonesStat.completeCount}/{milestonesStat.totalCount}
              </span>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
});

export default MapViewProjectCard;
